import * as React from 'react';
import * as Sentry from '@sentry/react';
import { browserTracingIntegration, replayIntegration, getReplay } from '@sentry/react';
import { enableMapSet } from 'immer';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { createRoot } from 'react-dom/client';
import { setupInterceptors } from '@studio/services/setup-interceptors';
import App from './App';
import AuthProvider from './components/AuthProvider';
import './i18n';
import {
  ldClientId,
  segmentClientId,
  LD_ANONYMOUS_CONTEXT,
  sentryEnvironment,
  sentrySampleRate,
  SENTRY_ENV_LOCAL_DEV,
} from './constants/api-constants';

if (sentryEnvironment !== SENTRY_ENV_LOCAL_DEV) {
  const ignoredErrors = [
    'A network error occurred.',
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
    'timeout of 0ms exceeded',
    'error loading dynamically imported module',
    'Importing a module script failed',
    'Failed to fetch dynamically imported module',
  ];

  Sentry.init({
    beforeSend: (event, hint) => {
      const originalException = hint.originalException;
      //@ts-expect-error originalException type is unknown
      if (originalException?.response?.status !== undefined && originalException.response.status === 0) {
        getReplay()?.stop();
        return null;
      }
      return event;
    },
    dsn: 'https://8b60072bca31426dbfa7320277c0b2a4@o1151571.ingest.sentry.io/6228448',
    environment: sentryEnvironment,
    integrations: [
      browserTracingIntegration(),
      replayIntegration({
        beforeErrorSampling: (event) => {
          const errorMessage = event.exception?.values?.[0]?.value;
          if (typeof errorMessage === 'string') {
            return !ignoredErrors.some((err) => errorMessage.includes(err));
          }
          return true;
        },
      }),
    ],
    release: import.meta.env.VITE_APP_VERSION,
    // Same as default, but explicitly set for clarity
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: sentrySampleRate,
    ignoreErrors: ignoredErrors,
    denyUrls: ['launchdarkly.com', /^https?:\/\/control\.resi\.io\/webplayer.*$/],
  });
}

(async () => {
  if (window.analytics) {
    window.analytics.load(segmentClientId);
  }

  enableMapSet();

  const LDProvider = await asyncWithLDProvider({ clientSideID: ldClientId, user: { ...LD_ANONYMOUS_CONTEXT } });

  const container = document.getElementById('root');
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const root = createRoot(container!);

  root.render(
    <React.StrictMode>
      <LDProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </LDProvider>
    </React.StrictMode>
  );
})();

setupInterceptors();
